import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

interface Plan {
  id: string;
  name: string;
  max_checks: number;
  max_users: number;
  max_alert_channels: number;
  price_monthly_cents: number;
  price_yearly_cents: number;
  trial_days: number;
  active: boolean;
}

function formatLimit(val: number): string {
  return val < 0 ? "Unlimited" : String(val);
}

export default function ChoosePlanPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ plans: Plan[] }>("/billing/plans")
      .then((r) => {
        const active = (r.plans || []).filter((p) => p.active);
        active.sort((a, b) => {
          const aFree = a.price_monthly_cents === 0 && a.price_yearly_cents === 0;
          const bFree = b.price_monthly_cents === 0 && b.price_yearly_cents === 0;
          if (aFree && !bFree) return -1;
          if (!aFree && bFree) return 1;
          return a.price_monthly_cents - b.price_monthly_cents;
        });
        setPlans(active);
      })
      .catch(() => {
        // If plans can't load, go to dashboard
        navigate("/");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading plans...</p>
      </div>
    );
  }

  const freePlan = plans.find(
    (p) => p.price_monthly_cents === 0 && p.price_yearly_cents === 0
  );
  const paidPlans = plans.filter(
    (p) => p.price_monthly_cents > 0 || p.price_yearly_cents > 0
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-2xl px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">Choose your plan</h1>
          <p className="text-muted-foreground mt-2">
            Get started with a free plan or try Pro with a 14-day free trial.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          {/* Free plan card */}
          {freePlan && (
            <div className="rounded-lg border bg-card p-6 flex flex-col">
              <h2 className="text-xl font-semibold">{freePlan.name}</h2>
              <p className="text-2xl font-bold mt-2">Free</p>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground flex-1">
                <li>{formatLimit(freePlan.max_checks)} checks</li>
                <li>{formatLimit(freePlan.max_users)} team members</li>
                <li>{formatLimit(freePlan.max_alert_channels)} alert channels</li>
              </ul>
              <button
                onClick={() => navigate("/")}
                className="mt-6 w-full rounded py-2 text-sm border border-primary text-primary hover:bg-primary/5 transition-colors"
              >
                Continue with Free
              </button>
            </div>
          )}

          {/* Paid plan cards */}
          {paidPlans.map((plan) => (
            <div
              key={plan.id}
              className="rounded-lg border-2 border-primary bg-card p-6 flex flex-col relative"
            >
              {plan.trial_days > 0 && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                  {plan.trial_days}-day free trial
                </span>
              )}
              <h2 className="text-xl font-semibold">{plan.name}</h2>
              <p className="text-2xl font-bold mt-2">
                ${plan.price_monthly_cents / 100}
                <span className="text-sm font-normal text-muted-foreground">/mo</span>
              </p>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground flex-1">
                <li>{formatLimit(plan.max_checks)} checks</li>
                <li>{formatLimit(plan.max_users)} team members</li>
                <li>{formatLimit(plan.max_alert_channels)} alert channels</li>
              </ul>
              <button
                onClick={() =>
                  navigate(
                    `/account/billing?plan=${encodeURIComponent(plan.name.toLowerCase())}`
                  )
                }
                className="mt-6 w-full rounded py-2 text-sm bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              >
                {plan.trial_days > 0
                  ? `Start ${plan.trial_days}-Day Free Trial`
                  : `Upgrade to ${plan.name}`}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          You can change your plan anytime from the billing page.
        </p>
      </div>
    </div>
  );
}
